import React, { useCallback, useEffect, useState, useContext } from "react";
import { ThemeContext } from "../../../../../context/ThemeContext";
import { postData } from "../../../../../services/Fetchnodeservices";
import { getStoredAdminId } from "../../../../../utils/adminSession";

// ===================== ICONS =====================
const WalletIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M22 6v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2z" />
    <path d="M22 10h-4a2 2 0 0 0-2 2v0a2 2 0 0 0 2 2h4" />
    <circle cx="18" cy="14" r="1" fill="currentColor" />
  </svg>
);

const CheckCircleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
    <polyline points="22 4 12 14.01 9 11.01" />
  </svg>
);

const ClockIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

const TrendingIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
    <polyline points="17 6 23 6 23 12" />
  </svg>
);

// ===================== STAT CARD =====================
const StatCard = ({ icon, title, value, color, theme }) => {
  return (
    <div
      style={{
        background: theme.surface,
        padding: 20,
        borderRadius: 16,
        boxShadow: `0 4px 12px ${theme.primary}10`,
        border: `1px solid ${theme.border}`,
        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
        textAlign: 'center',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = `0 8px 25px ${theme.primary}20`;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = `0 4px 12px ${theme.primary}10`;
      }}
    >
      <div
        style={{
          width: 48,
          height: 48,
          borderRadius: 14,
          background: `${color}1a`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: color,
          margin: '0 auto 12px',
        }}
      >
        {icon}
      </div>
      <h4 style={{ margin: '0 0 8px', fontSize: 12, color: theme.textSecondary, fontWeight: 500 }}>
        {title}
      </h4>
      <h2 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: theme.textPrimary }}>
        {value}
      </h2>
    </div>
  );
};

// ===================== MAIN COMPONENT =====================
export default function PendingCashDeposits() {
  const { theme } = useContext(ThemeContext); // ← Theme Context

  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [verifyingId, setVerifyingId] = useState(null);

  const [summary, setSummary] = useState({
    pending_count: 0,
    verified_count: 0,
    pending_amount: 0,
    verified_amount: 0,
  });

  const verifyCash = async (depositId) => {
    const adminId = getStoredAdminId();

    if (!adminId) {
      alert("Please login again");
      return;
    }

    setVerifyingId(depositId);

    const result = await postData(
      "vandersuperadmin/admin/cash/verify-deposit",
      {
        collection_id: depositId,
        admin_id: adminId,
      }
    );

    if (result.success) {
      alert("Cash Verified Successfully");
      fetchData();
      fetchSummary();
    } else {
      alert(result.message || result.error || "Cash verification failed");
    }

    setVerifyingId(null);
  };

  const fetchSummary = async () => {
    const result = await postData(
      "vandersuperadmin/admin/cash/dashboard-summary",
      {}
    );

    console.log("SUMMARY:", result);

    if (result.success && result.data.length > 0) {
      setSummary(result.data[0]);
    }
  };

  const fetchData = useCallback(async () => {
    setLoading(true);

    const result = await postData(
      "vandersuperadmin/admin/cash/pending-deposits",
      {}
    );
    console.log("API RESULT:", result);
    if (result.success) {
      setRows(result.data);
    } else {
      setRows([]);
    }

    setLoading(false);
  }, []);

  useEffect(() => {
    fetchData();
    fetchSummary();
  }, [fetchData]);

  return (
    <div style={{ padding: 24, minHeight: '100vh', background: theme.background, transition: 'background 0.3s ease' }}>
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>

      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: theme.textPrimary }}>
          ⏳ Pending Cash Verification
        </h2>
        <p style={{ margin: '4px 0 0', fontSize: 13, color: theme.textMuted }}>
          Review and verify pending cash deposits from drivers
        </p>
      </div>

      {/* Stats Cards */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          gap: 16,
          marginBottom: 24,
        }}
      >
        <StatCard
          icon={<WalletIcon />}
          title="Pending Amount"
          value={`₹ ${Number(summary.pending_amount || 0).toLocaleString()}`}
          color={theme.warning}
          theme={theme}
        />
        <StatCard
          icon={<TrendingIcon />}
          title="Verified Amount"
          value={`₹ ${Number(summary.verified_amount || 0).toLocaleString()}`}
          color={theme.success}
          theme={theme}
        />
        <StatCard
          icon={<ClockIcon />}
          title="Pending Requests"
          value={summary.pending_count}
          color={theme.warning}
          theme={theme}
        />
        <StatCard
          icon={<CheckCircleIcon />}
          title="Verified Requests"
          value={summary.verified_count}
          color={theme.success}
          theme={theme}
        />
      </div>

      {/* Table */}
      {loading ? (
        <div
          style={{
            textAlign: 'center',
            padding: 60,
            background: theme.surface,
            borderRadius: 16,
            border: `1px solid ${theme.border}`,
          }}
        >
          <div
            style={{
              width: 40,
              height: 40,
              border: `3px solid ${theme.border}`,
              borderTopColor: theme.primary,
              borderRadius: '50%',
              margin: '0 auto 16px',
              animation: 'spin 1s linear infinite',
            }}
          />
          <p style={{ color: theme.textSecondary, fontSize: 14 }}>
            Loading pending deposits...
          </p>
        </div>
      ) : (
        <div
          style={{
            background: theme.surface,
            borderRadius: 16,
            overflow: 'hidden',
            border: `1px solid ${theme.border}`,
            boxShadow: `0 4px 12px ${theme.primary}08`,
          }}
        >
          <div style={{ overflowX: 'auto' }}>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                minWidth: 900,
              }}
            >
              <thead>
                <tr style={{ background: theme.surfaceLighter }}>
                  <th style={thStyle(theme)}>#</th>
                  <th style={thStyle(theme)}>Driver</th>
                  <th style={thStyle(theme)}>Restaurant</th>
                  <th style={thStyle(theme)}>Order</th>
                  <th style={thStyle(theme)}>Collected By</th>
                  <th style={thStyle(theme)}>Amount</th>
                  <th style={thStyle(theme)}>Action</th>
                </tr>
              </thead>
              <tbody>
                {Array.isArray(rows) && rows.length > 0 ? (
                  rows.map((item, index) => (
                    <tr
                      key={item.collection_id}
                      style={{
                        borderBottom: `1px solid ${theme.border}`,
                        transition: 'background 0.2s ease',
                      }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.background =
                          theme.surfaceHover || `${theme.primary}08`)
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.background = 'transparent')
                      }
                    >
                      <td style={tdStyle(theme, theme.textSecondary)}>
                        {index + 1}
                      </td>
                      <td style={tdStyle(theme, theme.textPrimary, true)}>
                        {item.driver_name || "-"}
                      </td>
                      <td style={tdStyle(theme, theme.textSecondary)}>
                        {item.orders?.length
                          ? item.orders.map((o, i) => (
                              <div key={o.id}>
                                {o.restaurant_name}
                                {i < item.orders.length - 1 && (
                                  <span style={{ color: theme.textMuted, fontSize: 10 }}> • </span>
                                )}
                              </div>
                            ))
                          : "-"}
                      </td>
                      <td style={tdStyle(theme, theme.primary)}>
                        {item.orders?.length
                          ? item.orders.map((o) => (
                              <div
                                key={o.id}
                                style={{ fontFamily: 'monospace', fontSize: 12 }}
                              >
                                #{o.id}
                              </div>
                            ))
                          : "-"}
                      </td>
                      <td style={tdStyle(theme, theme.textSecondary)}>
                        {item.collected_by_admin_name || "-"}
                      </td>
                      <td style={{ ...tdStyle(theme, theme.success), fontWeight: 700, fontSize: 14 }}>
                        ₹ {Number(item.cash_amount || 0).toLocaleString()}
                      </td>
                      <td style={tdStyle(theme)}>
                        <button
                          onClick={() => verifyCash(item.collection_id)}
                          disabled={verifyingId === item.collection_id}
                          style={{
                            padding: "8px 16px",
                            background:
                              verifyingId === item.collection_id
                                ? theme.textMuted
                                : theme.success,
                            color: "#fff",
                            border: "none",
                            borderRadius: 8,
                            cursor: verifyingId === item.collection_id ? "not-allowed" : "pointer",
                            fontSize: 12,
                            fontWeight: 600,
                            transition: 'all 0.2s ease',
                            opacity: verifyingId === item.collection_id ? 0.6 : 1,
                            boxShadow: verifyingId === item.collection_id ? 'none' : `0 4px 12px ${theme.success}40`,
                          }}
                          onMouseEnter={(e) => {
                            if (verifyingId !== item.collection_id) {
                              e.currentTarget.style.transform = 'translateY(-1px)';
                              e.currentTarget.style.boxShadow = `0 6px 16px ${theme.success}60`;
                            }
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = verifyingId === item.collection_id ? 'none' : `0 4px 12px ${theme.success}40`;
                          }}
                        >
                          {verifyingId === item.collection_id
                            ? "⏳ Verifying..."
                            : "✓ Verify Cash"}
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan="7"
                      style={{
                        textAlign: "center",
                        padding: 60,
                      }}
                    >
                      <div style={{ fontSize: 48, marginBottom: 12 }}>💰</div>
                      <h3
                        style={{
                          color: theme.textPrimary,
                          marginBottom: 8,
                          fontSize: 16,
                          fontWeight: 600,
                        }}
                      >
                        No Pending Cash Deposits Found
                      </h3>
                      <p style={{ color: theme.textMuted, fontSize: 13, margin: 0 }}>
                        All cash deposits have been verified
                      </p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Footer */}
          {Array.isArray(rows) && rows.length > 0 && (
            <div
              style={{
                padding: '14px 20px',
                borderTop: `1px solid ${theme.border}`,
                background: theme.surfaceLighter,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 12,
              }}
            >
              <span style={{ fontSize: 12, color: theme.textSecondary }}>
                Showing <strong style={{ color: theme.textPrimary }}>{rows.length}</strong> pending deposits
              </span>
              <span style={{ fontSize: 12, color: theme.warning, fontWeight: 600 }}>
                Total Pending: ₹ {Number(summary.pending_amount || 0).toLocaleString()}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ===================== TABLE STYLES =====================
const thStyle = (theme) => ({
  padding: "14px 16px",
  textAlign: "left",
  fontSize: 11,
  fontWeight: 700,
  color: theme.primary,
  whiteSpace: "nowrap",
  borderBottom: `1px solid ${theme.border}`,
});

const tdStyle = (theme, color, isBold = false) => ({
  padding: "14px 16px",
  fontSize: 13,
  color: color || theme.textPrimary,
  fontWeight: isBold ? 600 : 400,
  whiteSpace: "nowrap",
});
